/* Elite custom start: Labor Day Coffee Duo builder. */
class EliteLaborDayDuo extends HTMLElement {
  #activeBag = 0;
  #selections = [null, null];
  #productCards = [];

  connectedCallback() {
    this.#productCards = [...this.querySelectorAll('[data-duo-product-card]')];
    this.#bindEvents();
    this.#render();
  }

  #bindEvents() {
    for (const button of this.querySelectorAll('[data-duo-bag-tab]')) {
      button.addEventListener('click', () => {
        this.#activeBag = Number(button.dataset.duoBagTab);
        this.#render();
      });
    }

    for (const card of this.#productCards) {
      card.querySelector('[data-duo-select-product]')?.addEventListener('click', () => {
        this.#selectProduct(card);
      });
    }

    this.querySelector('[data-duo-roast]')?.addEventListener('change', (event) => {
      const selection = this.#selections[this.#activeBag];
      if (!selection) return;
      selection.roast = event.target.value;
      selection.variant = this.#findVariant(selection);
      this.#render();
    });

    this.querySelector('[data-duo-grind]')?.addEventListener('change', (event) => {
      const selection = this.#selections[this.#activeBag];
      if (!selection) return;
      selection.grind = event.target.value;
      selection.variant = this.#findVariant(selection);
      this.#render();
    });

    this.querySelector('[data-duo-add]')?.addEventListener('click', () => this.#addToCart());
  }

  #selectProduct(card) {
    const data = JSON.parse(card.querySelector('[data-duo-product-json]').textContent);
    const availableVariants = data.variants.filter((variant) => variant.available);
    if (!availableVariants.length) return;

    const roastIndex = data.options.findIndex((option) => option.toLowerCase() === 'roast');
    const grindIndex = data.options.findIndex((option) => option.toLowerCase().includes('grind'));
    const firstVariant = availableVariants[0];
    const selection = {
      product: data,
      roastIndex,
      grindIndex,
      roast: roastIndex >= 0 ? firstVariant.options[roastIndex] : '',
      grind: grindIndex >= 0 ? firstVariant.options[grindIndex] : '',
      variant: firstVariant
    };

    this.#selections[this.#activeBag] = selection;
    if (this.#activeBag === 0 && !this.#selections[1]) this.#activeBag = 1;
    this.#render();
  }

  #findVariant(selection) {
    return selection.product.variants.find((variant) => {
      if (!variant.available) return false;
      const roastMatches = selection.roastIndex < 0 || variant.options[selection.roastIndex] === selection.roast;
      const grindMatches = selection.grindIndex < 0 || variant.options[selection.grindIndex] === selection.grind;
      return roastMatches && grindMatches;
    }) || null;
  }

  #optionValues(selection, optionIndex) {
    if (!selection || optionIndex < 0) return [];
    return [...new Set(selection.product.variants
      .filter((variant) => variant.available)
      .map((variant) => variant.options[optionIndex]))];
  }

  #renderSelect(select, values, currentValue) {
    if (!select) return;
    select.replaceChildren();
    for (const value of values) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      option.selected = value === currentValue;
      select.append(option);
    }
  }

  #render() {
    const activeSelection = this.#selections[this.#activeBag];
    const completedCount = this.#selections.filter((selection) => selection?.variant).length;

    for (const button of this.querySelectorAll('[data-duo-bag-tab]')) {
      const index = Number(button.dataset.duoBagTab);
      const selection = this.#selections[index];
      button.setAttribute('aria-selected', String(index === this.#activeBag));
      button.classList.toggle('elite-duo__bag-tab--active', index === this.#activeBag);
      button.querySelector('[data-duo-bag-name]').textContent = selection
        ? selection.product.title.replace(/\s+Coffee\s+1LB$/i, '')
        : 'Choose a coffee';
    }

    const progress = this.querySelector('[data-duo-progress]');
    if (progress) progress.textContent = `${completedCount} / 2 selected`;

    const configuration = this.querySelector('[data-duo-configuration]');
    const emptyState = this.querySelector('[data-duo-empty]');
    if (configuration) configuration.hidden = !activeSelection;
    if (emptyState) emptyState.hidden = Boolean(activeSelection);

    if (activeSelection) {
      this.#renderSelect(
        this.querySelector('[data-duo-roast]'),
        this.#optionValues(activeSelection, activeSelection.roastIndex),
        activeSelection.roast
      );
      this.#renderSelect(
        this.querySelector('[data-duo-grind]'),
        this.#optionValues(activeSelection, activeSelection.grindIndex),
        activeSelection.grind
      );
    }

    for (const card of this.#productCards) {
      const data = JSON.parse(card.querySelector('[data-duo-product-json]').textContent);
      const selectedFor = [];
      for (let index = 0; index < this.#selections.length; index += 1) {
        if (this.#selections[index]?.product.id === data.id) selectedFor.push(index + 1);
      }
      card.classList.toggle('elite-duo-product--selected', selectedFor.length > 0);
      const button = card.querySelector('[data-duo-select-product]');
      button.textContent = selectedFor.length
        ? `Selected for bag ${selectedFor.join(' & ')}`
        : `Choose for bag ${this.#activeBag + 1}`;
    }

    for (let index = 0; index < 2; index += 1) {
      const row = this.querySelector(`[data-duo-summary-row="${index}"]`);
      const selection = this.#selections[index];
      row.querySelector('[data-duo-summary-title]').textContent = selection
        ? selection.product.title.replace(/\s+Coffee\s+1LB$/i, '')
        : `Bag ${index + 1} is not selected`;
      row.querySelector('[data-duo-summary-meta]').textContent = selection?.variant
        ? [selection.roast, selection.grind].filter(Boolean).join(' · ')
        : 'Choose a coffee, roast, and grind';
      row.classList.toggle('elite-duo-summary__row--complete', Boolean(selection?.variant));
    }

    const addButton = this.querySelector('[data-duo-add]');
    if (addButton) addButton.disabled = completedCount !== 2;
  }

  async #addToCart() {
    const addButton = this.querySelector('[data-duo-add]');
    const status = this.querySelector('[data-duo-status]');
    if (!addButton || this.#selections.some((selection) => !selection?.variant)) return;

    addButton.disabled = true;
    addButton.setAttribute('aria-busy', 'true');
    status.textContent = this.dataset.loadingMessage;

    const quantities = new Map();
    for (const selection of this.#selections) {
      quantities.set(selection.variant.id, (quantities.get(selection.variant.id) || 0) + 1);
    }
    const items = [...quantities.entries()].map(([id, quantity]) => ({ id, quantity }));

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) throw new Error('Cart request failed');

      const discountCode = this.dataset.discountCode;
      const destination = discountCode
        ? `${window.Shopify.routes.root}discount/${encodeURIComponent(discountCode)}?redirect=/cart`
        : `${window.Shopify.routes.root}cart`;
      window.location.assign(destination);
    } catch (error) {
      status.textContent = this.dataset.errorMessage;
      addButton.disabled = false;
      console.error('Labor Day Coffee Duo:', error);
    } finally {
      addButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('elite-labor-day-duo')) {
  customElements.define('elite-labor-day-duo', EliteLaborDayDuo);
}
/* Elite custom end: Labor Day Coffee Duo builder. */
